/**
 * RepertoireService - Manages student song repertoire tracking
 *
 * Firestore Collection: repertoire
 * Structure:
 * {
 *   studentId: string,
 *   songName: string,
 *   songNameLower: string,
 *   canonicalSongId: string|null,  // `songs` registry doc id; null = UNLINKED
 *                                  // (explicit null, not absent, so the linking
 *                                  // surface can query for it). See
 *                                  // songRegistryModel.js for the identity
 *                                  // decision (2026-08-05).
 *   metadata: {
 *     title: string,
 *     artist: string,
 *     composers: [string],
 *     lyricists: [string],
 *     arrangers: [string],       // "arranged by" / "orchestrated by" / "orchestrator"
 *     album: string,
 *     releaseYear: number,
 *     genres: [string],
 *     isMusical: boolean,
 *     musicalName: string,
 *     yearWritten: number,
 *     musicbrainzId: string
 *   },
 *   videos: {                       // one saved video per KIND, so the next
 *     lyrics: string|null,          // teacher opens the song and everything is
 *     karaoke: string|null,         // already there (Anthony, 2026-08-31).
 *     performance: string|null,     // Song-level for the same reason lyrics are.
 *   },
 *   videoUrl: string|null,          // LEGACY single slot, pre-2026-08-31. Read as
 *                                  // the lyrics slot when `videos` is absent; never
 *                                  // written any more. Not migrated because it is
 *                                  // one or two rows from the first evening's use
 *                                  // and re-saving is a click.
 *   firstWorkedOn: timestamp,
 *   lastWorkedOn: timestamp,
 *   sessions: [                      // one entry per distinct lessonId
 *     {
 *       lessonId: string,
 *       date: string,
 *       notes: [{ text, status }]
 *     }
 *   ],
 *   createdAt: timestamp,
 *   updatedAt: timestamp
 * }
 *
 * Session count is DERIVED (distinct lessonIds), never stored — a stored
 * counter drifted badly under 2s-autosave upserts (see StudentRepertoire.js).
 */

import {
	collection,
	doc,
	addDoc,
	updateDoc,
	getDoc,
	getDocs,
	query,
	where,
	orderBy,
	serverTimestamp,
	arrayUnion,
	deleteField,
	writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';
import { ensureCanonicalSong } from './SongRegistryService';
import { pointersToHide } from './repertoireRemovalModel';

const COLLECTION_NAME = 'repertoire';

/**
 * Resolve the canonical song id for a row, or null.
 *
 * NEVER overwrites an existing canonicalSongId: merge/link corrections are
 * made by hand (question A: merging two songs = rewriting this field), and an
 * auto-restamp on the next add would silently undo them. Linking uses the
 * STORED metadata when the row has it — a human may have corrected it — and
 * falls back to the incoming blob, so any touch of a metadata-carrying row
 * self-heals an unlinked state.
 */
const resolveCanonicalSongId = async (existing, metadata) => {
	if (existing?.canonicalSongId) return undefined; // keep — do not write
	const source = existing?.metadata || metadata;
	if (!source) return null; // unlinked, explicitly
	return ensureCanonicalSong(source);
};

/**
 * Get all repertoire entries for a student. Rows removed by an admin
 * (removedAt, repertoireRemovalModel.js) are left out unless asked for: only
 * the profile's "Removed songs" list, where they are restored, asks.
 */
export const getStudentRepertoire = async (studentId, { includeRemoved = false } = {}) => {
	try {
		const q = query(
			collection(db, COLLECTION_NAME),
			where('studentId', '==', studentId),
			orderBy('lastWorkedOn', 'desc')
		);
		const snapshot = await getDocs(q);
		const rows = snapshot.docs.map((doc) => ({
			id: doc.id,
			...doc.data(),
		}));
		return includeRemoved ? rows : rows.filter((r) => !r.removedAt);
	} catch (error) {
		console.error('Error fetching student repertoire:', error);
		throw error;
	}
};

/**
 * Find a repertoire entry by student and song name
 */
export const findRepertoireEntry = async (studentId, songName) => {
	try {
		const normalizedName = songName.trim().toLowerCase();
		const q = query(
			collection(db, COLLECTION_NAME),
			where('studentId', '==', studentId),
			where('songNameLower', '==', normalizedName)
		);
		const snapshot = await getDocs(q);
		// A removed row is not "the entry": adding the song again starts a new
		// row rather than writing lessons into one an admin hid (a teacher
		// cannot clear removedAt, so reusing it would keep the song invisible).
		const live = snapshot.docs.find((d) => !d.data().removedAt);
		if (!live) return null;
		return {
			id: live.id,
			...live.data(),
		};
	} catch (error) {
		console.error('Error finding repertoire entry:', error);
		throw error;
	}
};

/**
 * Add or update a song in student's repertoire
 * If song exists, updates lastWorkedOn and adds session
 * If new, creates entry
 * @param {string} studentId - Student ID
 * @param {string} songName - Song name
 * @param {string} lessonId - Lesson ID
 * @param {string} lessonDate - Lesson date
 * @param {Array} notes - Array of note objects
 * @param {Object} metadata - Optional song metadata from MusicService
 */
export const trackSongInRepertoire = async (studentId, songName, lessonId, lessonDate, notes = [], metadata = null) => {
	try {
		const existing = await findRepertoireEntry(studentId, songName);

		// N1b: sessions[] records THAT a song was worked in a lesson, not WHAT was
		// said. Note text now lives in the `songNotes` collection, one document per
		// note, which is the only place status (and therefore visibility) is
		// authoritative. Two reasons this array must not carry note text:
		//
		//  1. A `captured` note is teacher-only by policy — it may hold feedback
		//     deliberately withheld from the student. Snapshotting note text into
		//     repertoire would put it in a second place with no status wall, which
		//     is exactly how it would leak into a student-readable history.
		//  2. Duplicated text drifts. A note edited or re-classified in songNotes
		//     would leave a stale copy here forever.
		//
		// This also answers the card's open sub-question "how a lesson records 'we
		// worked on this today' without duplicating note text": lessonId + date.
		const sessionData = {
			lessonId,
			date: lessonDate,
		};

		if (existing) {
			// Update existing entry. Upsert this lesson's snapshot by lessonId
			// instead of appending: every 2s autosave in a lesson re-runs this, and
			// arrayUnion can't replace-by-key, so appends inflated sessions[] with a
			// snapshot per autosave. A read-modify-write is correct here (single
			// client, 2s debounce, no concurrent writers). Collapsing existing
			// entries to the newest snapshot per lessonId also self-heals legacy
			// inflation on the next touch — no backfill needed.
			const docRef = doc(db, COLLECTION_NAME, existing.id);

			const byLesson = new Map();
			for (const s of existing.sessions || []) {
				// sessions[] is appended chronologically, so later wins = newest snapshot.
				if (s && s.lessonId) byLesson.set(s.lessonId, s);
			}
			byLesson.set(lessonId, sessionData); // replace, not append
			const sessions = Array.from(byLesson.values());

			const updateData = {
				lastWorkedOn: serverTimestamp(),
				sessions,
				updatedAt: serverTimestamp(),
			};

			// If metadata is provided and doesn't exist, add it
			if (metadata && !existing.metadata) {
				updateData.metadata = metadata;
			}

			// Link-on-touch: an unlinked row with usable metadata gets its
			// canonical song id stamped here too, so rows created before the
			// registry existed self-heal on the next worked lesson.
			const canonicalSongId = await resolveCanonicalSongId(existing, metadata);
			if (canonicalSongId !== undefined && canonicalSongId !== null) {
				updateData.canonicalSongId = canonicalSongId;
			}

			await updateDoc(docRef, updateData);
			return { id: existing.id, updated: true, hadMetadata: !!existing.metadata };
		} else {
			// Create new entry
			const canonicalSongId = await resolveCanonicalSongId(null, metadata);
			const docRef = await addDoc(collection(db, COLLECTION_NAME), {
				studentId,
				songName: songName.trim(),
				songNameLower: songName.trim().toLowerCase(),
				canonicalSongId: canonicalSongId ?? null,
				metadata: metadata || null,
				firstWorkedOn: serverTimestamp(),
				lastWorkedOn: serverTimestamp(),
				sessions: [sessionData],
				createdAt: serverTimestamp(),
				updatedAt: serverTimestamp(),
			});
			return { id: docRef.id, created: true, hadMetadata: !!metadata };
		}
	} catch (error) {
		console.error('Error tracking song in repertoire:', error);
		throw error;
	}
};

/**
 * Batch track multiple songs from a lesson's song notes
 * @param {string} studentId - Student ID
 * @param {string} lessonId - Lesson ID
 * @param {string} lessonDate - Lesson date
 * @param {Array} songs - Array of song objects with name, notes, and optional metadata
 */
export const trackSongsFromLesson = async (studentId, lessonId, lessonDate, songs) => {
	const results = [];
	for (const song of songs) {
		if (song.name && song.name.trim()) {
			const result = await trackSongInRepertoire(
				studentId,
				song.name,
				lessonId,
				lessonDate,
				song.notes || [],
				song.metadata || null
			);
			results.push({ songName: song.name, ...result });
		}
	}
	return results;
};

/**
 * Find or create the repertoire entry for a song, WITHOUT recording a session.
 *
 * N1b needs this because a Song Note is keyed on (student × song) and `songId` is
 * a repertoire document id. A song must therefore have a real, stable id the
 * moment a teacher adds it — before any note exists and before the lesson saves.
 * The old flow minted `Date.now()` ids in component state and only reached
 * repertoire on autosave, which is why "vowels" in two lessons were unrelated rows.
 *
 * Deliberately does not touch sessions[] or lastWorkedOn: adding a song to the
 * board is not the same as having worked it. trackSongInRepertoire still owns that.
 */
// In-flight ensures, by student + song name. Two adds of the same song that
// overlap (a double tap, two sheets) must not both find "no entry" and both
// write one: before 2026-10-22 that is how one student got three identical
// rows 50 ms apart. The second caller gets the first caller's answer.
const pendingEnsures = new Map();

export const ensureRepertoireEntry = async (studentId, songName, metadata = null) => {
	if (!studentId || !songName?.trim()) {
		throw new Error('ensureRepertoireEntry requires studentId and songName');
	}
	const key = `${studentId}::${songName.trim().toLowerCase()}`;
	if (pendingEnsures.has(key)) return pendingEnsures.get(key);
	const run = ensureRepertoireEntryOnce(studentId, songName, metadata).finally(() => pendingEnsures.delete(key));
	pendingEnsures.set(key, run);
	return run;
};

const ensureRepertoireEntryOnce = async (studentId, songName, metadata) => {
	const existing = await findRepertoireEntry(studentId, songName);
	if (existing) {
		// Backfill metadata we did not have the first time round, but never
		// overwrite metadata a human may have corrected by hand. Same for the
		// canonical song id: stamp it only when the row has none (link-on-touch;
		// see resolveCanonicalSongId for why an existing id is never replaced).
		const patch = {};
		if (metadata && !existing.metadata) {
			patch.metadata = metadata;
		}
		const canonicalSongId = await resolveCanonicalSongId(existing, metadata);
		if (canonicalSongId !== undefined && canonicalSongId !== null) {
			patch.canonicalSongId = canonicalSongId;
		}
		if (Object.keys(patch).length) {
			await updateDoc(doc(db, COLLECTION_NAME, existing.id), {
				...patch,
				updatedAt: serverTimestamp(),
			});
		}
		return { id: existing.id, created: false, entry: existing, canonicalSongId: existing.canonicalSongId || patch.canonicalSongId || null };
	}

	const canonicalSongId = await resolveCanonicalSongId(null, metadata);

	// The same song under another name ("Doors" vs "Doors Teddy Swims") is
	// still the same song: when the catalogue resolves it to a canonical id the
	// student already has, open that entry instead of starting a second one.
	if (canonicalSongId) {
		const sameSong = await getDocs(
			query(
				collection(db, COLLECTION_NAME),
				where('studentId', '==', studentId),
				where('canonicalSongId', '==', canonicalSongId)
			)
		);
		const live = sameSong.docs.find((d) => !d.data().removedAt);
		if (live) return { id: live.id, created: false, entry: { id: live.id, ...live.data() }, canonicalSongId };
	}

	const ref = await addDoc(collection(db, COLLECTION_NAME), {
		studentId,
		songName: songName.trim(),
		songNameLower: songName.trim().toLowerCase(),
		canonicalSongId: canonicalSongId ?? null,
		metadata: metadata || null,
		lyrics: null,
		sessions: [],
		firstWorkedOn: serverTimestamp(),
		lastWorkedOn: serverTimestamp(),
		createdAt: serverTimestamp(),
		updatedAt: serverTimestamp(),
	});
	return { id: ref.id, created: true, entry: null, canonicalSongId: canonicalSongId ?? null };
};

/**
 * Persist lyrics on the SONG, not the note.
 *
 * Lyrics were living at `lessons.songNotes[].lyrics` — per-lesson, so the same
 * song's lyrics were re-fetched and re-stored for every lesson. They are a
 * property of the song, so they belong on the surviving song-level record.
 *
 * N1a §2 is explicit that lyrics are NOT part of the songNotes entity, and warns
 * that a careless third lyrics store is easy to create here: the recital system
 * (RecitalSongs.js) already keeps its own independent one. This is the second
 * store, not the third — do not add another.
 */
/**
 * Every repertoire row pointing at one canonical song — i.e. every student who
 * has worked it. THIS IS THE JOIN THE RECITAL PAGE EXISTS ON: it is what
 * "pull everyone singing this song into its rehearsal" finally means, and it did
 * not exist before the registry shipped (2026-08-05).
 *
 * SINGLE-FIELD EQUALITY, so Firestore serves it from the automatic single-field
 * index and no composite index can be forgotten. Do NOT add an `orderBy` here —
 * ordering belongs to the caller, in memory, over a handful of rows.
 *
 * Unlinked rows (canonicalSongId null) are invisible to this query by design.
 * That is the incentive to link them, not a gap to paper over with a title match.
 */
export const getRepertoireForSong = async (canonicalSongId) => {
	if (!canonicalSongId) return [];
	const snapshot = await getDocs(
		query(collection(db, COLLECTION_NAME), where('canonicalSongId', '==', canonicalSongId))
	);
	return snapshot.docs.map((d) => ({ id: d.id, ...d.data() })).filter((r) => !r.removedAt);
};

/**
 * Remove a song from a student's repertoire: HIDE it, restorably (Anthony,
 * 2026-09-27). One batch stamps the row, that song's Song Notes and its track
 * pointers (pointersToHide: never one another visible row of the same title
 * still claims). Admin / owner only; the rules refuse anyone else. Returns
 * the counts so the caller can say what went.
 */
export const removeRepertoireEntry = async (entry, { uid, visibleEntries = [] } = {}) => {
	if (!entry || !entry.id || !entry.studentId) throw new Error('Nothing to remove.');
	const [notesSnap, tracksSnap] = await Promise.all([
		getDocs(query(collection(db, 'songNotes'), where('studentId', '==', entry.studentId), where('songId', '==', entry.id))),
		getDocs(query(collection(db, 'studentTracks'), where('studentId', '==', entry.studentId))),
	]);
	const pointers = pointersToHide(
		tracksSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
		entry,
		visibleEntries
	);
	const notes = notesSnap.docs.filter((d) => !d.data().removedAt);
	const stamp = { removedAt: serverTimestamp(), removedBy: uid || null };
	const batch = writeBatch(db);
	batch.update(doc(db, COLLECTION_NAME, entry.id), stamp);
	notes.forEach((d) => batch.update(d.ref, { ...stamp, removedWith: entry.id }));
	pointers.forEach((p) => batch.update(doc(db, 'studentTracks', p.id), { ...stamp, removedWith: entry.id }));
	await batch.commit();
	return { notes: notes.length, tracks: pointers.length };
};

/** Put back exactly what removeRepertoireEntry(entry) hid: the row, and the
 *  notes and pointers stamped removedWith = its id. */
export const restoreRepertoireEntry = async (entry) => {
	if (!entry || !entry.id || !entry.studentId) throw new Error('Nothing to restore.');
	const [notesSnap, tracksSnap] = await Promise.all([
		getDocs(query(collection(db, 'songNotes'), where('studentId', '==', entry.studentId), where('removedWith', '==', entry.id))),
		getDocs(query(collection(db, 'studentTracks'), where('studentId', '==', entry.studentId), where('removedWith', '==', entry.id))),
	]);
	const clear = { removedAt: deleteField(), removedBy: deleteField(), removedWith: deleteField() };
	const batch = writeBatch(db);
	batch.update(doc(db, COLLECTION_NAME, entry.id), { removedAt: deleteField(), removedBy: deleteField() });
	notesSnap.docs.forEach((d) => batch.update(d.ref, clear));
	tracksSnap.docs.forEach((d) => batch.update(d.ref, clear));
	await batch.commit();
	return { notes: notesSnap.size, tracks: tracksSnap.size };
};

/**
 * Link an UNLINKED repertoire row to a canonical song (the link half of the
 * link/merge flow, Anthony 2026-08-06: link first).
 *
 * THE GUARD IS THE MIRROR IMAGE OF `resolveCanonicalSongId`'s. That one refuses
 * to overwrite a non-null canonicalSongId so that a merge, once made, is not
 * silently undone by the next 2s-autosave touch. This one refuses for the same
 * reason from the other side: linking is a null -> id transition ONLY. Changing
 * an id that already exists is a MERGE — a different tool, with a survivor to
 * choose and other students' rows to sweep — and it is the one writer allowed
 * to do it. A link control that could quietly repoint a linked row would be
 * that merge, minus every safeguard.
 *
 * Throws (rather than no-opping) on an already-linked row: the caller reached
 * here from a "Not linked" badge, so a row that is linked by the time the write
 * lands means the screen is stale, and silently doing nothing would report
 * success for a link that never happened.
 *
 * Metadata is written only when the row has NONE — same rule as
 * trackSongInRepertoire. A row whose metadata a human corrected must not be
 * overwritten by whatever the search happened to return.
 */
export const linkRepertoireEntryToSong = async (entryId, canonicalSongId, metadata = null) => {
	if (!entryId) throw new Error('linkRepertoireEntryToSong: entryId is required');
	if (!canonicalSongId) throw new Error('linkRepertoireEntryToSong: canonicalSongId is required');

	const docRef = doc(db, COLLECTION_NAME, entryId);
	const snapshot = await getDoc(docRef);
	if (!snapshot.exists()) throw new Error('That song is no longer in this repertoire.');

	const existing = snapshot.data() || {};
	if (existing.canonicalSongId) {
		throw new Error('That song is already linked. Changing an existing link is a merge.');
	}

	const patch = { canonicalSongId, updatedAt: serverTimestamp() };
	if (metadata && !existing.metadata) patch.metadata = metadata;

	await updateDoc(docRef, patch);
	return { id: entryId, canonicalSongId, metadataWritten: !!patch.metadata };
};

export const updateSongLyrics = async (repertoireId, lyrics) => {
	if (!repertoireId) return;
	await updateDoc(doc(db, COLLECTION_NAME, repertoireId), {
		lyrics: lyrics || null,
		updatedAt: serverTimestamp(),
	});
};

export const VIDEO_KINDS = ['lyrics', 'karaoke', 'performance'];

/**
 * Persist ONE of the song's videos — same home and same reasoning as lyrics.
 *
 * ── A DOT PATH, NOT A NESTED MAP, AND THIS IS THE WHOLE POINT ──
 * `set({ videos: {...} }, { merge: true })` reads as "add these keys" and means
 * "make `videos` exactly this" — merge protects sibling TOP-LEVEL fields and
 * replaces a nested map wholesale. That is precisely how the lesson-caps
 * migration deleted the one live cap on 2026-08-30 and then reported success.
 * A dot path touches only the key it names and cannot clear its siblings, so
 * saving a karaoke video can never wipe the lyrics one.
 *
 * Empty clears to null rather than storing '', so "not set" is one value
 * everywhere and the reader's `|| null` cannot disagree with the doc.
 */
export const updateSongVideo = async (repertoireId, kind, url) => {
	if (!repertoireId) return;
	if (!VIDEO_KINDS.includes(kind)) {
		throw new Error(`updateSongVideo: unknown video kind "${kind}"`);
	}
	await updateDoc(doc(db, COLLECTION_NAME, repertoireId), {
		[`videos.${kind}`]: url || null,
		updatedAt: serverTimestamp(),
	});
};

/**
 * A teacher's nudge of one section marker on one track timeline (2026-09-28,
 * LessonDashboard/tracks/sectionMarkers.js). ONE DOT PATH per mark, never a
 * merged map: `sectionMarks` holds every timeline's marks, and a map write
 * would replace its siblings (CLAUDE.md, "{ merge: true } is not deep").
 * `seconds` null removes the nudge, and the automatic time returns.
 */
export const setSectionMark = async (repertoireId, markKey, sectionId, seconds) => {
	if (!repertoireId || !/^m[a-z0-9]+$/.test(String(markKey || '')) || !/^[A-Za-z0-9_-]+$/.test(String(sectionId || ''))) return;
	const value = seconds === null || seconds === undefined ? deleteField() : Math.max(0, Math.round(Number(seconds) * 10) / 10);
	await updateDoc(doc(db, COLLECTION_NAME, repertoireId), {
		[`sectionMarks.${markKey}.${sectionId}`]: value,
		updatedAt: serverTimestamp(),
	});
};

/**
 * A track's trim, start / end points in seconds with no new file (2026-09-28,
 * LessonDashboard/tracks/TrimControl.js), on the same timeline key as the
 * section marks. One dot path per timeline; `trim` null removes it.
 */
export const setTrackTrim = async (repertoireId, markKey, trim) => {
	if (!repertoireId || !/^m[a-z0-9]+$/.test(String(markKey || ''))) return;
	const round = (n) => Math.max(0, Math.round(Number(n) * 10) / 10);
	const value = trim ? { start: round(trim.start || 0), end: trim.end === null || trim.end === undefined ? null : round(trim.end) } : deleteField();
	await updateDoc(doc(db, COLLECTION_NAME, repertoireId), {
		[`trims.${markKey}`]: value,
		updatedAt: serverTimestamp(),
	});
};

/**
 * Recital / Audition tracks (2026-09-27, trackRolesModel). The recital track
 * is ONE file per student x song; choosing another moves the old one into
 * recitalTrackHistory (arrayUnion), because a recital cut changes only for a
 * reason (Anthony: "unless it needs to be cut somewhere down the line for
 * time"). `ref` null clears it. Stamped with who and when.
 */
export const setRecitalTrack = async (repertoireId, ref, previous, uid) => {
	if (!repertoireId) return;
	const patch = {
		recitalTrack: ref ? { ...ref, setBy: uid || null, setAt: new Date().toISOString() } : null,
		updatedAt: serverTimestamp(),
	};
	if (previous && previous.storagePath && (!ref || previous.storagePath !== ref.storagePath)) {
		patch.recitalTrackHistory = arrayUnion({ ...previous, replacedAt: new Date().toISOString() });
	}
	await updateDoc(doc(db, COLLECTION_NAME, repertoireId), patch);
};

/** The whole auditionTracks list (toggledAuditions builds it). */
export const setAuditionTracks = async (repertoireId, list) => {
	if (!repertoireId) return;
	await updateDoc(doc(db, COLLECTION_NAME, repertoireId), {
		auditionTracks: Array.isArray(list) ? list : [],
		updatedAt: serverTimestamp(),
	});
};

export default {
	removeRepertoireEntry,
	restoreRepertoireEntry,
	getStudentRepertoire,
	findRepertoireEntry,
	trackSongInRepertoire,
	trackSongsFromLesson,
	ensureRepertoireEntry,
	getRepertoireForSong,
	linkRepertoireEntryToSong,
	updateSongLyrics,
	updateSongVideo,
};
